import { t } from "@lingui/core/macro";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import { getArticles } from "../-data/articles";

type RelatedArticlesProps = {
  /** Article slugs to feature, in order. Unknown slugs are skipped. */
  slugs?: string[];
  /** Slug of the page being viewed, so it never links to itself. */
  currentSlug?: string;
  /** Cap on how many cards to render once filtering is done. */
  limit?: number;
  heading?: string;
};

/**
 * Contextual internal links out of a leaf page. Every article and landing page
 * ends in one, which turns the blog from a set of orphaned leaves into a
 * connected cluster - crawlers follow the links, and readers get a next step
 * that is not the back button.
 *
 * When no `slugs` are given it falls back to a rotation through the catalogue,
 * so a page can opt in without curating a list.
 */
export function RelatedArticles({ slugs, currentSlug, limit = 3, heading }: RelatedArticlesProps) {
  const articles = getArticles();

  const curated = slugs
    ?.map((slug) => articles.find((article) => article.slug === slug))
    .filter((article) => article !== undefined) ?? [];

  // An article page with no curated list rotates through the catalogue starting
  // just after itself, instead of falling back to "the newest N". Newest-N gave
  // the same three articles every uncurated link on the site - they were pulling
  // 12-15 internal links each while older posts sat on one, from the hub alone -
  // whereas rotating hands every article an equal share of link equity and still
  // reads as a sensible "keep reading" queue. Pages with no `currentSlug` (the
  // hubs) have no position to rotate from, so they keep the newest-first order.
  const position = articles.findIndex((article) => article.slug === currentSlug);

  const fallback =
    position === -1
      ? [...articles].sort((a, b) => b.date.localeCompare(a.date))
      : articles.map((_, index) => articles[(position + 1 + index) % articles.length]);

  const selected = [...curated, ...fallback]
    .filter((article) => article.slug !== currentSlug)
    // `curated` and `fallback` overlap - keep the curated position, drop the repeat.
    .filter((article, index, list) => list.findIndex((a) => a.slug === article.slug) === index)
    .slice(0, limit);

  if (selected.length === 0) return null;

  return (
    <section className="border-t border-border p-4 md:p-8 xl:py-12">
      <div className="mx-auto max-w-3xl space-y-6">
        <h2 className="text-lg font-semibold tracking-tight md:text-xl">
          {heading ?? t`Keep reading`}
        </h2>

        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {selected.map((article) => (
            <li key={article.slug}>
              <Link
                to="/articles/$slug"
                params={{ slug: article.slug }}
                className="group flex h-full flex-col gap-3 rounded-lg border border-border p-4 transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="w-fit rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                  {article.tag}
                </span>

                <span className="flex-1 text-sm font-semibold leading-snug tracking-tight group-hover:text-primary">
                  {article.title}
                </span>

                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {article.readTime}
                  <ArrowRightIcon
                    size={12}
                    aria-hidden="true"
                    className="transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
